import React from 'react';
import PropTypes from 'prop-types';
import RadarList from 'components/Radar/List/List';
import gql from 'graphql-tag';
import { Query, Mutation } from 'components/common/Query';
import { adopt } from 'react-adopt';
import {
  defaultNumberOfRows as DEFAULT_NUMBER_OF_ROWS,
  defaultProcessorListFilters,
} from 'config';
import qs, { getQueryString } from 'helpers/queryStrings';
import ContentLoader from 'react-content-loader';
import { PageInfo } from 'components/common/Paginator/PaginatorContainer';
import RouterContext from 'helpers/routerContext';
import getProcessorTargetVariables from 'helpers/processorTargetVariables';

export const RADAR_LIST_QUERY = gql`
  query PROCESSOR_LIST(
    $listName: [FilterListName]
    $group: String
    $first: Int
    $after: String
    $last: Int
    $before: String
    $value: String
    $target: [FilterProcessorListTarget]
  ) {
    processorList(
      listName: $listName
      group: $group
      first: $first
      after: $after
      last: $last
      before: $before
      value: $value
      target: $target
    ) {
      pageInfo {
        ...PaginatorPageInfo
      }
      edges {
        node {
          id
          listName
          value
          group
          description
          updatedBy {
            email
            fullname
          }
          createdBy {
            email
            fullname
          }
        }
      }
    }
  }
  ${PageInfo}
`;

export const PROCESSOR_LIST_NAMES = gql`
  query ProcessorListNames($target: RuleTargets) {
    processorListNames(target: $target) {
      name
      description
    }
  }
`;
export const PROCESSOR_LIST_GROUPS = gql`
  query {
    processorListGroups
  }
`;

export const PROCESSOR_LIST_ITEM_DELETE = gql`
  mutation($ids: [ID]!) {
    deleteProcessorListItem(input: { ids: $ids }) {
      successIds

      errors {
        field
        messages
      }
    }
  }
`;

class RadarListContainer extends React.Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    const {
      numberOfRowsSelected,
      direction,
      cursor,
      ...restOfFilters
    } = props.initialFilters;
    const defaultFilters = {
      listName: defaultProcessorListFilters.listName,
      group: defaultProcessorListFilters.group,
      q: '',
    };

    if (restOfFilters.listName) {
      restOfFilters.listName = restOfFilters.listName
        ? restOfFilters.listName.toLowerCase()
        : restOfFilters.listName;
    }

    this.state = {
      appliedFilters: { ...defaultFilters, ...restOfFilters },
      numberOfRowsSelected:
        parseInt(numberOfRowsSelected, 0) || DEFAULT_NUMBER_OF_ROWS,
      pagination: { direction: direction || null, cursor: cursor || undefined },
      serverErrors: [],
    };
    this.Compose = adopt({
      radarList: this.RadarListQuery,
      processorListNames: this.ProcessorNamesQuery,
      processorListGroups: this.ProcessorGroupsQuery,
      deleteItem: this.ProcessorMutationDelete,
    });
  }

  getRadarListQueryVariables = () => {
    const { appliedFilters, pagination, numberOfRowsSelected } = this.state;
    const { target } = this.props;
    const paginationVariables =
      !pagination.direction || pagination.direction === 'NEXT'
        ? {
            first: numberOfRowsSelected,
            after: pagination.cursor,
          }
        : {
            last: numberOfRowsSelected,
            before: pagination.cursor,
          };

    return {
      ...paginationVariables,
      listName:
        appliedFilters.listName === 'all'
          ? undefined
          : appliedFilters.listName.toUpperCase(),
      group: appliedFilters.group === 'all' ? undefined : appliedFilters.group,
      value: appliedFilters.q === '' ? undefined : appliedFilters.q,
      ...getProcessorTargetVariables(target),
    };
  };

  RadarListQuery = ({ render }) => (
    <Query
      query={RADAR_LIST_QUERY}
      variables={this.getRadarListQueryVariables()}
      fetchPolicy="cache-and-network"
    >
      {({ data, loading }) => render({ data, loading })}
    </Query>
  );

  ProcessorNamesQuery = ({ render }) => {
    const { target } = this.props;

    return (
      <Query
        query={PROCESSOR_LIST_NAMES}
        variables={getProcessorTargetVariables(target)}
        fetchPolicy="cache-and-network"
      >
        {({ data, loading }) =>
          render({ listNames: data, listLoading: loading })
        }
      </Query>
    );
  };

  ProcessorGroupsQuery = ({ render }) => (
    <Query
      query={PROCESSOR_LIST_GROUPS}
      variables={{}}
      fetchPolicy="cache-and-network"
    >
      {({ data, loading }) =>
        render({ listGroups: data, groupsLoading: loading })
      }
    </Query>
  );

  ProcessorMutationDelete = ({ render }) => (
    <Mutation
      mutation={PROCESSOR_LIST_ITEM_DELETE}
      onCompleted={this.ProcessorDeleteComplete}
      update={(cache, { data: { deleteProcessorListItem } }) => {
        const queryVariables = this.getRadarListQueryVariables();
        const { processorList } = cache.readQuery({
          query: RADAR_LIST_QUERY,
          variables: queryVariables,
        });

        cache.writeQuery({
          query: RADAR_LIST_QUERY,
          variables: queryVariables,
          data: {
            processorList: {
              ...processorList,
              edges: processorList.edges.filter(
                edge =>
                  deleteProcessorListItem.successIds.indexOf(edge.node.id) ===
                  -1,
              ),
            },
          },
        });
      }}
    >
      {handleDelete => render({ handleDelete })}
    </Mutation>
  );

  ProcessorDeleteComplete = async ({ deleteProcessorListItem }) => {
    const { errors } = deleteProcessorListItem;
    if (errors) {
      this.setState({ serverErrors: errors });
    }
  };

  handleFilters = filter => {
    const history = this.context;
    const { appliedFilters } = this.state;
    const newFilters = { ...appliedFilters, ...filter };

    this.setState({
      appliedFilters: newFilters,
      pagination: { direction: null, cursor: undefined },
    });

    const currentLocation = history.location.pathname;
    const { numberOfRowsSelected } = getQueryString(history);

    const urlString = qs.stringify({
      ...newFilters,
      numberOfRowsSelected,
      direction: undefined,
      cursor: undefined,
    });

    history.push({ pathname: currentLocation, search: urlString });
  };

  /**
   * @description
   * Fetch more rows when user change page.
   *
   * @param {Object} paginatorVariables - Pagination query's variables.
   */
  handleChangePage = ({ direction, cursor }) => {
    const history = this.context;

    this.setState({ pagination: { direction, cursor } });
    const currentLocation = history.location.pathname;
    const queryString = getQueryString(history);
    const urlString = qs.stringify({
      ...queryString,
      direction,
      cursor,
    });
    history.push({ pathname: currentLocation, search: urlString });
  };

  /**
   * @description
   * Change number of rows selected.
   *
   * @param {number} selectedItem - New number of rows selected.
   */
  handleChangeNumberOfRows = selectedItem => {
    const history = this.context;

    this.setState({
      numberOfRowsSelected: selectedItem,
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const queryString = getQueryString(history);
    const urlString = qs.stringify({
      ...queryString,
      numberOfRowsSelected: selectedItem,
      direction: undefined,
      cursor: undefined,
    });
    history.push({ pathname: currentLocation, search: urlString });
  };

  render() {
    const { routePaths, titleKey } = this.props;
    const {
      appliedFilters,
      numberOfRowsSelected,
      pagination,
      serverErrors,
    } = this.state;
    return (
      <this.Compose>
        {({
          radarList: { data, loading },
          processorListNames: { listNames, listLoading },
          processorListGroups: { listGroups, groupsLoading },
          deleteItem: { handleDelete },
        }) => {
          if (data && Object.keys(data).length === 0) {
            return (
              <div style={{ padding: '0 20%' }}>
                <ContentLoader
                  height={391}
                  width={666}
                  speed={2}
                  primaryColor="#d9d9d9"
                  secondaryColor="#ecebeb"
                >
                  <path
                    // eslint-disable-next-line max-len
                    d="M41,145 L141,145 C142.656854,145 144,146.343146 144,148 L144,162 C144,163.656854 142.656854,165 141,165 L41,165 C39.3431458,165 38,163.656854 38,162 L38,148 C38,146.343146 39.3431458,145 41,145 Z M41,177 L519,177 C520.656854,177 522,178.343146 522,180 L522,206 C522,207.656854 520.656854,209 519,209 L41,209 C39.3431458,209 38,207.656854 38,206 L38,180 C38,178.343146 39.3431458,177 41,177 Z M553,141 L660,141 C661.656854,141 663,142.343146 663,144 L663,158 C663,159.656854 661.656854,161 660,161 L553,161 C551.343146,161 550,159.656854 550,158 L550,144 C550,142.343146 551.343146,141 553,141 Z M553,178 L591,178 C592.656854,178 594,179.343146 594,181 L594,207 C594,208.656854 592.656854,210 591,210 L553,210 C551.343146,210 550,208.656854 550,207 L550,181 C550,179.343146 551.343146,178 553,178 Z M623,178 L661,178 C662.656854,178 664,179.343146 664,181 L664,207 C664,208.656854 662.656854,210 661,210 L623,210 C621.343146,210 620,208.656854 620,207 L620,181 C620,179.343146 621.343146,178 623,178 Z M2,149 L14,149 C15.1045695,149 16,149.895431 16,151 L16,163 C16,164.104569 15.1045695,165 14,165 L2,165 C0.8954305,165 1.3527075e-16,164.104569 0,163 L0,151 C-1.3527075e-16,149.895431 0.8954305,149 2,149 Z M2,185 L14,185 C15.1045695,185 16,185.895431 16,187 L16,199 C16,200.104569 15.1045695,201 14,201 L2,201 C0.8954305,201 1.3527075e-16,200.104569 0,199 L0,187 C-1.3527075e-16,185.895431 0.8954305,185 2,185 Z M41,214 L519,214 C520.656854,214 522,215.343146 522,217 L522,243 C522,244.656854 520.656854,246 519,246 L41,246 C39.3431458,246 38,244.656854 38,243 L38,217 C38,215.343146 39.3431458,214 41,214 Z M553,214.2 L591,214.2 C592.656854,214.2 594,215.543146 594,217.2 L594,243.2 C594,244.856854 592.656854,246.2 591,246.2 L553,246.2 C551.343146,246.2 550,244.856854 550,243.2 L550,217.2 C550,215.543146 551.343146,214.2 553,214.2 Z M623,214.2 L661,214.2 C662.656854,214.2 664,215.543146 664,217.2 L664,243.2 C664,244.856854 662.656854,246.2 661,246.2 L623,246.2 C621.343146,246.2 620,244.856854 620,243.2 L620,217.2 C620,215.543146 621.343146,214.2 623,214.2 Z M2,222 L14,222 C15.1045695,222 16,222.895431 16,224 L16,236 C16,237.104569 15.1045695,238 14,238 L2,238 C0.8954305,238 1.3527075e-16,237.104569 0,236 L0,224 C-1.3527075e-16,222.895431 0.8954305,222 2,222 Z M41,250 L519,250 C520.656854,250 522,251.343146 522,253 L522,279 C522,280.656854 520.656854,282 519,282 L41,282 C39.3431458,282 38,280.656854 38,279 L38,253 C38,251.343146 39.3431458,250 41,250 Z M553,250.4 L591,250.4 C592.656854,250.4 594,251.743146 594,253.4 L594,279.4 C594,281.056854 592.656854,282.4 591,282.4 L553,282.4 C551.343146,282.4 550,281.056854 550,279.4 L550,253.4 C550,251.743146 551.343146,250.4 553,250.4 Z M623,250.4 L661,250.4 C662.656854,250.4 664,251.743146 664,253.4 L664,279.4 C664,281.056854 662.656854,282.4 661,282.4 L623,282.4 C621.343146,282.4 620,281.056854 620,279.4 L620,253.4 C620,251.743146 621.343146,250.4 623,250.4 Z M2,258 L14,258 C15.1045695,258 16,258.895431 16,260 L16,272 C16,273.104569 15.1045695,274 14,274 L2,274 C0.8954305,274 1.3527075e-16,273.104569 0,272 L0,260 C-1.3527075e-16,258.895431 0.8954305,258 2,258 Z M41,286 L519,286 C520.656854,286 522,287.343146 522,289 L522,315 C522,316.656854 520.656854,318 519,318 L41,318 C39.3431458,318 38,316.656854 38,315 L38,289 C38,287.343146 39.3431458,286 41,286 Z M553,286.6 L591,286.6 C592.656854,286.6 594,287.943146 594,289.6 L594,315.6 C594,317.256854 592.656854,318.6 591,318.6 L553,318.6 C551.343146,318.6 550,317.256854 550,315.6 L550,289.6 C550,287.943146 551.343146,286.6 553,286.6 Z M623,286.6 L661,286.6 C662.656854,286.6 664,287.943146 664,289.6 L664,315.6 C664,317.256854 662.656854,318.6 661,318.6 L623,318.6 C621.343146,318.6 620,317.256854 620,315.6 L620,289.6 C620,287.943146 621.343146,286.6 623,286.6 Z M2,294 L14,294 C15.1045695,294 16,294.895431 16,296 L16,308 C16,309.104569 15.1045695,310 14,310 L2,310 C0.8954305,310 1.3527075e-16,309.104569 0,308 L0,296 C-1.3527075e-16,294.895431 0.8954305,294 2,294 Z M41,323 L519,323 C520.656854,323 522,324.343146 522,326 L522,352 C522,353.656854 520.656854,355 519,355 L41,355 C39.3431458,355 38,353.656854 38,352 L38,326 C38,324.343146 39.3431458,323 41,323 Z M553,322.8 L591,322.8 C592.656854,322.8 594,324.143146 594,325.8 L594,351.8 C594,353.456854 592.656854,354.8 591,354.8 L553,354.8 C551.343146,354.8 550,353.456854 550,351.8 L550,325.8 C550,324.143146 551.343146,322.8 553,322.8 Z M623,322.8 L661,322.8 C662.656854,322.8 664,324.143146 664,325.8 L664,351.8 C664,353.456854 662.656854,354.8 661,354.8 L623,354.8 C621.343146,354.8 620,353.456854 620,351.8 L620,325.8 C620,324.143146 621.343146,322.8 623,322.8 Z M2,331 L14,331 C15.1045695,331 16,331.895431 16,333 L16,345 C16,346.104569 15.1045695,347 14,347 L2,347 C0.8954305,347 1.3527075e-16,346.104569 0,345 L0,333 C-1.3527075e-16,331.895431 0.8954305,331 2,331 Z M41,359 L519,359 C520.656854,359 522,360.343146 522,362 L522,388 C522,389.656854 520.656854,391 519,391 L41,391 C39.3431458,391 38,389.656854 38,388 L38,362 C38,360.343146 39.3431458,359 41,359 Z M553,359 L591,359 C592.656854,359 594,360.343146 594,362 L594,388 C594,389.656854 592.656854,391 591,391 L553,391 C551.343146,391 550,389.656854 550,388 L550,362 C550,360.343146 551.343146,359 553,359 Z M623,359 L661,359 C662.656854,359 664,360.343146 664,362 L664,388 C664,389.656854 662.656854,391 661,391 L623,391 C621.343146,391 620,389.656854 620,388 L620,362 C620,360.343146 621.343146,359 623,359 Z M2,367 L14,367 C15.1045695,367 16,367.895431 16,369 L16,381 C16,382.104569 15.1045695,383 14,383 L2,383 C0.8954305,383 1.3527075e-16,382.104569 0,381 L0,369 C-1.3527075e-16,367.895431 0.8954305,367 2,367 Z M438.532526,90 L656.105599,90 C660.523877,90 664.105599,93.581722 664.105599,98 L664.105599,118 C664.105599,122.418278 660.523877,126 656.105599,126 L438.532526,126 C434.114248,126 430.532526,122.418278 430.532526,118 L430.532526,98 C430.532526,93.581722 434.114248,90 438.532526,90 Z M7,0 L247,0 C250.865993,-7.10171439e-16 254,3.13400675 254,7 L254,32 C254,35.8659932 250.865993,39 247,39 L7,39 C3.13400675,39 4.73447626e-16,35.8659932 0,32 L0,7 C-4.73447626e-16,3.13400675 3.13400675,2.48652828e-15 7,1.77635684e-15 Z M501.5,0 L646.5,0 C657.269553,-1.97833472e-15 666,8.73044738 666,19.5 C666,30.2695526 657.269553,39 646.5,39 L501.5,39 C490.730447,39 482,30.2695526 482,19.5 C482,8.73044738 490.730447,1.97833472e-15 501.5,0 Z"
                    fill="#E5EAEC"
                  />
                </ContentLoader>
              </div>
            );
          }
          return (
            <RadarList
              data={data}
              routePaths={routePaths}
              titleKey={titleKey}
              listNames={listNames.processorListNames}
              listGroups={listGroups.processorListGroups}
              appliedFilters={appliedFilters}
              filter={this.handleFilters}
              loadingData={Object.keys(data).length === 0}
              fullLoading={loading || listLoading || groupsLoading}
              numberOfRowsSelected={numberOfRowsSelected}
              paginationDirection={pagination.direction}
              changePage={paginatorVariables => {
                this.handleChangePage(paginatorVariables);
              }}
              changeNumberOfRows={this.handleChangeNumberOfRows}
              deleteItem={itemId =>
                handleDelete({
                  variables: {
                    ids: [itemId],
                  },
                })
              }
              errors={serverErrors}
            />
          );
        }}
      </this.Compose>
    );
  }
}

RadarListContainer.propTypes = {
  initialFilters: PropTypes.instanceOf(Object).isRequired,
  routePaths: PropTypes.shape({}),
  target: PropTypes.string,
  titleKey: PropTypes.string,
};

RadarListContainer.defaultProps = {
  routePaths: undefined,
  target: undefined,
  titleKey: undefined,
};

export default RadarListContainer;
